import {
    getLocation,
    push,
    LOCATION_CHANGE,
    replace,
} from 'connected-react-router';
import { stringify, parse } from 'query-string';
import _ from 'lodash';
import genericStatsService from '../../services/genericStatsService';
import { setLoading, setErrorMessage } from './ui';
import { logout } from './user';
import { getDefaultFrom, getDefaultTo, getGroupingPeriod } from '../storeUtils/dateUtils';
import routeConstants from '../../Routes/routeConstants';
import { isUnauthorized } from '../../services/utils/responseValidator';

// ACTIONS
const CLEAR_LEADS_DATA = 'CLEAR_LEADS_DATA';
const CLEAR_LEADS_FORM = 'CLEAR_LEADS_FORM';
const GROUP_LEADS_BY = 'GROUP_LEADS_BY';
const SET_LEADS_FORM_STATE = 'SET_LEADS_FORM_STATE';
const SET_LEADS_FORM_FIELD = 'SET_LEADS_FORM_FIELD';
const SET_FORMATED_LEADS_DATA = 'SET_FORMATED_LEADS_DATA';
const SET_LEADS_LOCATION = 'SET_LEADS_LOCATION';
const SET_SELECTED_LEADS_GRAPH_KEYS = 'SET_SELECTED_LEADS_GRAPH_KEYS';

const initialState = {
    formState: {
        product: [],
        source: [],
        page: [],
        from: getDefaultFrom(),
        to: getDefaultTo(),
        isCustomer: false,
    },
    selectedGraphKeys: null,
    groupBy: 'MONTH',
    location: {
        pathname: routeConstants.LEADS_STATS,
        hash: '',
        search: '',
    },
    stats: {},
};

// REDUCER
const reducer = (state = initialState, action) => {
    switch (action.type) {
    case SET_FORMATED_LEADS_DATA: {
        const clone = _.cloneDeep(action.value);
        const {
            graphDataPoint,
            graphDate,
        } = action.value;

        return Object.assign({}, state, {
            stats: clone,
            graphDataPoint: graphDataPoint[state.groupBy.toLowerCase()],
            graphDate: graphDate[state.groupBy.toLowerCase()],
        });
    }
    case GROUP_LEADS_BY: {
        const {
            graphDataPoint,
            graphDate,
        } = state.stats;

        return Object.assign({}, state, {
            groupBy: action.value,
            graphDataPoint: graphDataPoint && graphDataPoint[action.value.toLowerCase()],
            graphDate: graphDate && graphDate[action.value.toLowerCase()],
        });
    }
    case SET_LEADS_FORM_STATE: {
        return Object.assign({}, state, {
            formState: action.value,
        });
    }
    case SET_LEADS_FORM_FIELD: {
        return Object.assign({}, state, {
            formState: { ...state.formState, ...action.value },
        });
    }
    case SET_LEADS_LOCATION: {
        return Object.assign({}, state, {
            location: action.value,
        });
    }
    case CLEAR_LEADS_DATA: {
        return Object.assign({}, state, {
            stats: {},
            graphDataPoint: null,
            graphDate: null,
            selectedGraphKeys: null,
        });
    }
    case CLEAR_LEADS_FORM: {
        return Object.assign({}, state, {
            formState: initialState.formState,
            graphDataPoint: null,
            graphDate: null,
        });
    }
    case SET_SELECTED_LEADS_GRAPH_KEYS: {
        return Object.assign({}, state, {
            selectedGraphKeys: action.value,
        });
    }
    case LOCATION_CHANGE: {
        const { pathname, state: actionPayloadState } = action.payload.location || {};
        const isLeadsStatsLocation = pathname === routeConstants.LEADS_STATS;

        if (isLeadsStatsLocation) {
            const { leadsstats } = actionPayloadState || {};
            const {
                graphDataPoint,
                graphDate,
                selectedGraphKeys,
                formState,
                groupBy,
                stats,
            } = leadsstats || initialState;

            return Object.assign({}, state, {
                graphDataPoint,
                graphDate,
                selectedGraphKeys,
                location: action.payload.location,
                formState,
                groupBy,
                stats,
            });
        }
        return state;
    }
    default:
        return state;
    }
};

// ACTION CREATORS
const clearData = () => ({
    type: CLEAR_LEADS_DATA,
});

const clearForm = () => ({
    type: CLEAR_LEADS_FORM,
});

const groupStatsBy = groupBy => ({
    type: GROUP_LEADS_BY,
    value: groupBy,
});

const internalSetFormattedData = stats => ({
    type: SET_FORMATED_LEADS_DATA,
    value: stats,
});

const setFormField = field => ({
    type: SET_LEADS_FORM_FIELD,
    value: field,
});

const internalSetFormState = formData => ({
    type: SET_LEADS_FORM_STATE,
    value: formData,
});

const internalSetLeadsLocation = location => ({
    type: SET_LEADS_LOCATION,
    value: location,
});

const setSelectedGraphKeys = selected => ({
    type: SET_SELECTED_LEADS_GRAPH_KEYS,
    value: selected,
});

// THUNK
const clearAll = () => (dispatch, getState) => {
    dispatch(clearData());
    dispatch(clearForm());
    const { formState } = getState().leadsstats;
    dispatch(push({
        pathname: routeConstants.LEADS_STATS,
        search: stringify(formState),
        state: getState(),
    }));
};

const getLeadsStats = formData => (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(clearData());
    dispatch(internalSetFormState(formData));
    genericStatsService.getLeadsStats(formData)
        .then((apiResonse) => {
            dispatch(groupStatsBy(getGroupingPeriod(formData.from, formData.to)));
            dispatch(internalSetFormattedData(apiResonse));
            dispatch(setLoading(false));
            dispatch(push({
                pathname: routeConstants.LEADS_STATS,
                search: stringify(formData),
                state: getState(),
            }));
            dispatch(internalSetLeadsLocation(getLocation(getState())));
        })
        .catch((err) => {
            dispatch(setLoading(false));
            if (isUnauthorized(err.response)) {
                dispatch(logout());
            }
            dispatch(setErrorMessage(err.message));
        });
};

const setInitialLeadsFormState = location => (dispatch, getState) => {
    const { formState } = getState().leadsstats;
    const formParams = parse(location.search);

    const newFormState = {
        product: formParams.product || formState.product,
        source: formParams.source || formState.source,
        page: formParams.page || formState.page,
        to: formParams.to || formState.to,
        from: formParams.from || formState.from,
        isCustomer: !!formParams.isCustomer && formParams.isCustomer === 'true',
    };

    dispatch(internalSetFormState(newFormState));

    dispatch(replace({
        pathname: routeConstants.LEADS_STATS,
        search: stringify(newFormState),
        state: getState(),
    }));

    dispatch(getLeadsStats(newFormState));
};

export {
    getLeadsStats,
    setInitialLeadsFormState,
    groupStatsBy,
    setFormField,
    clearData,
    clearForm,
    setSelectedGraphKeys,
    clearAll,
};

export default reducer;
